import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, History as HistoryIcon } from 'lucide-react';
import { TaskCard } from '@/components/domain/shared/TaskCard';
import { EventCard } from '@/components/domain/shared/EventCard';
import { TaskEventDetailModal } from '@/components/domain/shared/TaskEventDetailModal';
import { ItemFormModal, type ItemFormData } from '@/pages/review/ItemFormModal';
import { ScheduleTabSkeleton } from '@/components/states/PageSkeletons';
import { ErrorState } from '@/components/states/ErrorState';
import { EmptyState } from '@/components/states/EmptyState';
import { IconButton } from '@/components/ui';
import { scheduleService } from '@/services/schedule.service';
import { buildSeriesDelete } from '@/lib/seriesDelete';
import { getChildren, getMembers } from '@/services/household.service';
import { useAppShell } from '@/hooks/useAppShell';
import { usePermissions } from '@/hooks';
import { useUserStore } from '@/stores/userStore';
import { getTodayKey, toIsoWithTz } from '@/lib/tz';
import { buildSubtaskSummary } from '@/lib/subtasks';
import {
  buildHistoryGroups,
  formatHistoryDateHeader,
  getChildrenFromTask,
  getChildrenFromEvent,
  getMembersFromTask,
  getMembersFromEvent,
  getSourceSubject,
  taskToReviewItem,
  eventToReviewItem,
  type TaskRow,
  type EventRow,
} from '@/lib/schedule';
import type { Child, HouseholdMemberWithUser } from '@/types';

const HISTORY_WINDOW_DAYS = 30;

function subtractDaysKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d - days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`;
}

type HistoryItem =
  | { kind: 'task'; data: TaskRow }
  | { kind: 'event'; data: EventRow };

export function History() {
  const navigate = useNavigate();
  const { householdId, householdResolved, refreshChrome } = useAppShell();
  const { canEdit, canComplete, canDelete } = usePermissions();
  const userTimezone = useUserStore((s) => s.timezone);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [subtasksByEvent, setSubtasksByEvent] = useState<
    Map<string, TaskRow[]>
  >(new Map());
  const [householdChildren, setHouseholdChildren] = useState<Child[]>([]);
  const [householdMembers, setHouseholdMembers] = useState<
    HouseholdMemberWithUser[]
  >([]);
  const [detailItem, setDetailItem] = useState<HistoryItem | null>(null);
  const [editingItem, setEditingItem] = useState<HistoryItem | null>(null);

  // Only the newest loadData may write state: an older response landing
  // after a newer one (an edit's reload racing a toggle's) would put back
  // rows the parent just changed.
  const loadSeqRef = useRef(0);
  const loadData = useCallback(async () => {
    // Intrinsic guard rather than one at each of the five call sites: every
    // read below is bound to this id, and without it RLS would widen the
    // query to every household the user belongs to.
    if (!householdId) return;
    const seq = ++loadSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      const toKey = getTodayKey(userTimezone);
      const fromKey = subtractDaysKey(toKey, HISTORY_WINDOW_DAYS);
      const [taskData, eventData] = await Promise.all([
        scheduleService.getHistoryTasks(
          householdId,
          fromKey,
          toKey,
          userTimezone,
        ),
        scheduleService.getHistoryEvents(
          householdId,
          fromKey,
          toKey,
          userTimezone,
        ),
      ]);
      const evs = eventData as EventRow[];
      const relatedTasks = (await scheduleService.getTasksByRelatedEventIds(
        householdId,
        evs.map((e) => e.id),
      )) as TaskRow[];
      if (seq !== loadSeqRef.current) return;
      const map = new Map<string, TaskRow[]>();
      for (const t of relatedTasks) {
        if (!t.related_event_id) continue;
        const bucket = map.get(t.related_event_id);
        if (bucket) bucket.push(t);
        else map.set(t.related_event_id, [t]);
      }
      setTasks(taskData as TaskRow[]);
      setEvents(evs);
      setSubtasksByEvent(map);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Failed to load history:', err);
      setError(err instanceof Error ? err : new Error('Unable to load items.'));
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [householdId, userTimezone]);

  useEffect(() => {
    if (!householdId) {
      // Only stop showing the skeleton once resolution has FINISHED.
      // Otherwise the first paint (id still pending) renders this page's
      // empty/default state before the real data can possibly arrive.
      if (householdResolved) setLoading(false);
      return;
    }
    let cancelled = false;
    // Fire both at once. The roster needs nothing but householdId, which
    // is already in scope, so queueing it behind the day's data delayed it
    // for no reason (ScheduleTab already loads it concurrently).
    void loadData();
    // Deliberately NOT part of the skeleton gate: loadData owns
    // setLoading, and a roster failure stays non-fatal, exactly as before.
    void (async () => {
      try {
        const [kids, members] = await Promise.all([
          getChildren(householdId),
          getMembers(householdId),
        ]);
        if (!cancelled) {
          setHouseholdChildren(kids);
          setHouseholdMembers(members);
        }
      } catch (err) {
        console.error('Failed to load household children/members:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, householdResolved, loadData]);

  const handleEditItem = useCallback(
    async (data: ItemFormData) => {
      if (!editingItem) return;
      const childIds = data.child_names
        .map((name) => householdChildren.find((c) => c.name === name)?.id)
        .filter((id): id is string => typeof id === 'string');
      const toIso = (dt: string) => toIsoWithTz(dt, userTimezone);

      if (editingItem.kind === 'task') {
        const raw = data.due_datetime ?? data.start_datetime ?? null;
        await scheduleService.updateTask(editingItem.data.id, {
          title: data.title,
          due_date: raw ? toIso(raw) : null,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          notes: data.description ?? null,
          location: data.location ?? null,
          links: data.links,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
          // YAR-309: the form has emitted this since YAR-295 wired up the
          // "Related Event" dropdown; every handleEditItem simply never
          // forwarded it, so clearing or changing the link was a no-op.
          related_event_id: data.related_event_id,
        });
      } else {
        await scheduleService.updateEvent(editingItem.data.id, {
          title: data.title,
          start_date: data.start_datetime
            ? toIso(data.start_datetime)
            : editingItem.data.start_date,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          location: data.location ?? null,
          links: data.links,
          notes: data.description ?? null,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
        });
      }
      setEditingItem(null);
      try {
        if (householdId) await loadData();
      } finally {
        // An edit can move a task onto (or off) today: the nav's Today
        // badge has to re-count now, not on its 30 s poll.
        refreshChrome?.();
      }
    },
    [
      editingItem,
      householdChildren,
      householdId,
      loadData,
      refreshChrome,
      userTimezone,
    ],
  );

  const handleToggleTask = useCallback(
    async (taskId: string, currentStatus: string) => {
      try {
        await scheduleService.toggleTaskStatus(taskId, currentStatus);
        if (householdId) await loadData();
        // A task restored here goes back on Today; the nav badge has to
        // count it (YAR-476).
        refreshChrome?.();
      } catch (err) {
        console.error('Failed to toggle task:', err);
      }
    },
    [householdId, loadData, refreshChrome],
  );

  const todayKey = getTodayKey(userTimezone);
  const groups = useMemo(
    () => buildHistoryGroups(tasks, events, userTimezone),
    [tasks, events, userTimezone],
  );
  const isEmpty = !loading && !error && groups.length === 0;

  const footer = (
    <p className="text-center text-sm text-muted-foreground pt-2">
      Showing past events and completed tasks from the last 30 days.
    </p>
  );

  return (
    <div className="flex flex-col min-h-full bg-surface">
      {/* Header */}
      <div className="sticky top-0 z-[100] bg-background border-b border-border">
        <div className="flex items-center px-3 py-3">
          <div className="flex-1">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Back"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft size={22} />
            </IconButton>
          </div>
          <span className="text-lg font-semibold text-foreground">History</span>
          <div className="flex-1" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        {loading && (
          <div className="p-5 flex-1">
            <ScheduleTabSkeleton />
            {footer}
          </div>
        )}

        {!loading && error && (
          <ErrorState
            message="Unable to load items."
            onRetry={() => {
              if (householdId) loadData();
            }}
          />
        )}

        {isEmpty && (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState
              icon={
                <HistoryIcon
                  size={32}
                  aria-hidden
                />
              }
              title="Nothing here yet"
              description="Past events and completed tasks show up here from the last 30 days."
            />
          </div>
        )}

        {!loading && !error && !isEmpty && (
          <div className="p-5 flex flex-col gap-5 pb-10">
            {groups.map((group) => (
              <section
                key={group.dateKey}
                className="flex flex-col gap-2"
              >
                <h2 className="text-base font-bold text-foreground">
                  {formatHistoryDateHeader(group.dateKey, todayKey)}
                </h2>
                {group.items.map((item) => {
                  if (item.type === 'event') {
                    const event = item.data;
                    const subs = subtasksByEvent.get(event.id) ?? [];
                    return (
                      <EventCard
                        key={event.id}
                        id={event.id}
                        title={event.title}
                        description={event.notes ?? undefined}
                        startDate={event.start_date}
                        endDate={event.end_date ?? undefined}
                        isTimeUnknown={
                          event.source_review_item?.is_time_unknown ?? false
                        }
                        location={event.location ?? undefined}
                        link={event.link ?? undefined}
                        children={getChildrenFromEvent(event).map((c) => ({
                          name: c.name,
                          emoji: c.emoji_avatar ?? undefined,
                          color: c.emoji_color ?? undefined,
                          imagePath: c.avatar_image_path ?? undefined,
                        }))}
                        sourceSubject={getSourceSubject(event)}
                        // Anchors the date badge to the day the span ended and
                        // swaps the meta line to the compact range (YAR-176
                        // AC 12). `muted` stays the styling flag it always was.
                        variant="history"
                        muted
                        subtaskSummary={buildSubtaskSummary(subs)}
                        onClick={() =>
                          setDetailItem({ kind: 'event', data: event })
                        }
                        timezone={userTimezone}
                      />
                    );
                  }
                  const task = item.data;
                  return (
                    <TaskCard
                      key={task.id}
                      id={task.id}
                      title={task.title}
                      description={task.notes ?? undefined}
                      dueDate={task.due_date ?? undefined}
                      location={task.location ?? undefined}
                      link={task.link ?? undefined}
                      status={
                        task.status as
                          | 'TODO'
                          | 'IN_PROGRESS'
                          | 'DONE'
                          | 'CANCELLED'
                      }
                      children={getChildrenFromTask(task).map((c) => ({
                        name: c.name,
                        emoji: c.emoji_avatar ?? undefined,
                        color: c.emoji_color ?? undefined,
                        imagePath: c.avatar_image_path ?? undefined,
                      }))}
                      sourceSubject={getSourceSubject(task)}
                      relatedEventTitle={
                        task.related_event_id
                          ? events.find((e) => e.id === task.related_event_id)
                              ?.title
                          : undefined
                      }
                      muted
                      onToggleComplete={
                        canComplete
                          ? () => handleToggleTask(task.id, task.status)
                          : undefined
                      }
                      onClick={() =>
                        setDetailItem({ kind: 'task', data: task })
                      }
                      timezone={userTimezone}
                    />
                  );
                })}
              </section>
            ))}
            {footer}
          </div>
        )}
      </div>

      {detailItem && (
        <TaskEventDetailModal
          item={detailItem}
          canEdit={canEdit}
          muted
          timezone={userTimezone}
          relatedEventTitle={
            detailItem.kind === 'task' && detailItem.data.related_event_id
              ? events.find((e) => e.id === detailItem.data.related_event_id)
                  ?.title
              : undefined
          }
          subtasks={
            detailItem.kind === 'event'
              ? subtasksByEvent.get(detailItem.data.id)
              : undefined
          }
          onClose={() => setDetailItem(null)}
          onEdit={() => {
            setEditingItem(detailItem);
            setDetailItem(null);
          }}
          onToggleComplete={
            canComplete
              ? (id, status) => handleToggleTask(id, status)
              : undefined
          }
        />
      )}

      {editingItem && (
        <ItemFormModal
          mode="edit"
          initialType={editingItem.kind}
          initialData={
            editingItem.kind === 'task'
              ? taskToReviewItem(editingItem.data)
              : eventToReviewItem(editingItem.data)
          }
          // YAR-295: was hardcoded `[]`, which hid the "Related Event" field
          // entirely. History holds the last 30 days, so the options here are
          // past events — correct for editing a past item. Exclude the item
          // being edited so an event can't link to itself.
          availableEvents={events
            .filter((e) => e.id !== editingItem.data.id)
            .map((e) => ({ id: e.id, title: e.title }))}
          availableChildren={householdChildren.map((c) => ({
            name: c.name,
            emoji_avatar: c.emoji_avatar,
            emoji_color: c.emoji_color,
            avatar_image_path: c.avatar_image_path,
          }))}
          availableMembers={householdMembers
            .filter((m) => m.users)
            .map((m) => ({
              user_id: m.user_id,
              display_name: m.users!.display_name ?? m.users!.email ?? '',
              email: m.users!.email,
              avatar_emoji: m.users!.avatar_emoji,
              avatar_color: m.users!.avatar_color,
              avatar_image_path: m.users!.avatar_image_path,
            }))}
          initialMemberIds={
            editingItem.kind === 'task'
              ? getMembersFromTask(editingItem.data).map((m) => m.id)
              : getMembersFromEvent(editingItem.data).map((m) => m.id)
          }
          onSubmit={handleEditItem}
          onBack={() => setEditingItem(null)}
          seriesDelete={buildSeriesDelete({
            event: editingItem.kind === 'event' ? editingItem.data : null,
            householdId,
            canDelete,
            onDeleted: async () => {
              setEditingItem(null);
              try {
                if (householdId) await loadData();
              } finally {
                refreshChrome?.();
              }
            },
          })}
          onDelete={
            canDelete
              ? async () => {
                  if (editingItem.kind === 'task') {
                    await scheduleService.deleteTask(editingItem.data.id);
                  } else {
                    await scheduleService.deleteEvent(editingItem.data.id);
                  }
                  setEditingItem(null);
                  try {
                    if (householdId) await loadData();
                  } finally {
                    refreshChrome?.();
                  }
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
